import React, { useState, useEffect, createContext, useContext } from "react";
import { IntlProvider, createIntl, createIntlCache } from "react-intl";
import LanguageIcon from "@mui/icons-material/Language";
import TranslateIcon from "@mui/icons-material/Translate";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { IconButton } from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/en"
import "dayjs/locale/fi"

const supportedLanguages = ["en", "fi"];

type MessageObject = { [key: string]: string };
type Locale = "en" | "fi";
type Children = React.ReactNode;

const cache = createIntlCache();

const messages: { [key in Locale]: MessageObject } = {
  en: {} as MessageObject,
  fi: {} as MessageObject,
};

const importTranslations = async (locale: string, fileName: string) => {
  const translations = await import(`./${locale}/${fileName}`);
  return translations.default;
};

const initializeTranslations = async () => {
  for (const lang of supportedLanguages) {
    const translations = await importTranslations(lang, "translation.json");
    messages[lang as keyof typeof messages] = {
      ...translations,
    };
  }
};

interface LocaleContextProps {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextProps>({
  locale: "en",
  setLocale: () => {},
});

export const I18nProvider = ({ children }: { children: Children }) => {
  const [locale, setLocale] = useState<Locale>("en");
  const [, setIntl] = useState(  // [intl, setIntl], intl is not used, thus an empty state.
    createIntl({ locale: "en", messages: messages["en"] }, cache)
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userLang = navigator.language.slice(0, 2);
    if (supportedLanguages.includes(userLang)) {
      setLocale(userLang as Locale);
    }
    initializeTranslations().then(() => {
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    const newIntl = createIntl({ locale, messages: messages[locale] }, cache);
    setIntl(newIntl);
    dayjs.locale(locale);
    localStorage.setItem("language", locale);
    dispatchEvent(new Event("languageChange"));
  }, [locale]);

  if (isLoading) {
    return <div>Loading translations...</div>;
  }

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <IntlProvider locale={locale} messages={messages[locale]}>
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
};

export const LanguageSwitcher = () => {
  const { locale, setLocale } = useContext(LocaleContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (newLocale: Locale) => {
    setLocale(newLocale);
    localStorage.setItem("language", newLocale);
  };

  return (
    <div>
      <IconButton onClick={handleClick} sx={{ p: 1 }}>
        <TranslateIcon sx={{ fontSize: { xs: 14, md: 17, lg: 20 }, mb: 3 }} />
        <LanguageIcon sx={{ fontSize: { xs: 25, md: 30, lg: 40 } }} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={() => handleLanguageChange("en")}
          disabled={locale === "en"}
          sx={{ fontSize: { xs: 25, md: 30, lg: 40 } }}
        >
          🇺🇸
        </MenuItem>
        <MenuItem
          onClick={() => handleLanguageChange("fi")}
          disabled={locale === "fi"}
          sx={{ fontSize: { xs: 25, md: 30, lg: 40 } }}
        >
          🇫🇮
        </MenuItem>
      </Menu>
    </div>
  );
};
